import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bot, Cpu, Loader2, Sparkles } from "lucide-react";
import { triggerAI, setAIStatus } from "../../lib/api";
import { supabase } from "../../lib/supabase";
import { useCurrentEventRole } from "../../hooks/useCurrentEventRole";
import { can } from "../../lib/permissions";
import { TOOL_ICONS } from "../../lib/toolDefaults";
import type { Event, EventTool, Question, Response } from "../../lib/types";

interface Props {
  event: Event;
  question: Question | null;
  /** Visible responses of the displayed/active question. */
  responses: Response[];
  tools: EventTool[];
  hasClusters: boolean;
}

const CLUSTER_LABEL = "Cluster werden gebildet…";

/**
 * The stage's AI persona anchor: a bot tile + status label at the left edge
 * of the insight ticker. Everyone sees the tile and the idle/running state;
 * signed-in facilitators (trigger_ai) can click it to run Cluster and the
 * enabled insight tools directly from the stage.
 */
export default function StageAIAnchor({ event, question, responses, tools, hasClusters }: Props) {
  const { role } = useCurrentEventRole(event.id);
  const canTrigger = can(role, "trigger_ai");

  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState<string | null>(null); // "cluster" | tool.id
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // The menu is portalled to <body>: the ticker shell clips with
  // overflow-hidden and the stage content above stacks over it, so an
  // in-place absolute popover renders clipped/behind. Fixed-position portal
  // escapes both. Position is derived from the anchor rect at open time.
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; bottom: number } | null>(null);

  const aiActive = !!event.ai_status;
  const statusLabel = aiActive ? (event.ai_status_label ?? "KI denkt…") : "KI hört zu …";

  // Close on click-outside / Escape while open. The menu lives in a portal,
  // so "outside" means outside BOTH the anchor button and the menu itself.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggleOpen = () => {
    setOpen((o) => {
      if (!o && rootRef.current) {
        const rect = rootRef.current.getBoundingClientRect();
        setMenuPos({ left: rect.left, bottom: window.innerHeight - rect.top + 12 });
      }
      return !o;
    });
  };

  const visibleTexts = responses.map((r) => r.text);

  const runCluster = async () => {
    if (!question || running) return;
    if (visibleTexts.length < 3) {
      setErrorMsg("Mindestens 3 sichtbare Antworten nötig, um zu clustern.");
      return;
    }
    setRunning("cluster");
    setErrorMsg(null);
    const { data } = await supabase.auth.getSession();
    const accessToken = data.session?.access_token ?? "";
    await setAIStatus(event.id, "cluster", CLUSTER_LABEL);
    try {
      await triggerAI({
        type: "cluster",
        questionId: question.id,
        accessToken,
        triggerReason: hasClusters ? "manual" : "initial",
      });
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "KI-Anfrage fehlgeschlagen");
    } finally {
      await setAIStatus(event.id, null, null);
      setRunning(null);
    }
  };

  const runTool = async (tool: EventTool) => {
    if (!question || running) return;
    setRunning(tool.id);
    setErrorMsg(null);
    const { data } = await supabase.auth.getSession();
    const accessToken = data.session?.access_token ?? "";
    await setAIStatus(event.id, tool.tool_key, `${tool.label} wird ausgeführt…`);
    try {
      await triggerAI({
        type: "insight",
        toolId: tool.id,
        force: true,
        questionId: question.id,
        accessToken,
      });
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "KI-Anfrage fehlgeschlagen");
    } finally {
      await setAIStatus(event.id, null, null);
      setRunning(null);
    }
  };

  const tile = (
    <span
      className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
        aiActive ? "bg-rc-berry/20 text-rc-berry animate-shimmer" : "bg-rc-paper/8 text-rc-paper/60"
      } ${canTrigger ? "group-hover:bg-rc-paper/15 group-hover:text-rc-paper" : ""}`}
    >
      <Bot style={{ width: 18, height: 18 }} />
    </span>
  );

  const label = (
    <span
      className={`text-[11px] font-semibold tracking-[0.22em] uppercase whitespace-nowrap ${
        aiActive ? "text-rc-berry" : "text-rc-paper/50"
      }`}
    >
      {statusLabel}
    </span>
  );

  if (!canTrigger) {
    return (
      <div className="flex items-center gap-3">
        {tile}
        {label}
      </div>
    );
  }

  const enabledTools = tools.filter((t) => t.is_enabled);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="KI-Tools ausführen"
        className="group flex items-center gap-3 cursor-pointer"
      >
        {tile}
        {label}
      </button>

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: "fixed", left: menuPos.left, bottom: menuPos.bottom }}
          className="w-72 bg-rc-graphite border border-rc-paper/10 rounded-rc-lg shadow-cardLg p-2 z-[100] font-sans"
        >
          {!question && (
            <p className="text-[12px] text-rc-stone px-3 py-2">Keine aktive Frage.</p>
          )}
          {question && (
            <>
              <MenuRow
                icon={running === "cluster" ? Loader2 : Cpu}
                spinning={running === "cluster"}
                label={hasClusters ? "Neu clustern" : "Clustern"}
                disabled={!!running}
                onClick={runCluster}
              />
              {enabledTools.length > 0 && <div className="h-px bg-rc-paper/8 my-1.5 mx-2" />}
              {enabledTools.map((tool) => {
                const Icon = running === tool.id ? Loader2 : (TOOL_ICONS[tool.icon] ?? Sparkles);
                return (
                  <MenuRow
                    key={tool.id}
                    icon={Icon}
                    spinning={running === tool.id}
                    label={tool.label}
                    badge={tool.scope === "event" ? "Event" : undefined}
                    disabled={!!running}
                    onClick={() => runTool(tool)}
                  />
                );
              })}
              {errorMsg && (
                <p className="text-[11.5px] text-rc-danger px-3 pt-2 pb-1 leading-[1.4]">{errorMsg}</p>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

function MenuRow({
  icon: Icon,
  spinning,
  label,
  badge,
  disabled,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  spinning: boolean;
  label: string;
  badge?: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-lg text-[13.5px] font-semibold text-rc-paper/90 hover:bg-rc-paper/8 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${spinning ? "animate-spin" : ""}`} />
      <span className="flex-1 truncate">{label}</span>
      {badge && (
        <span className="text-[9px] font-semibold text-rc-berry uppercase tracking-[0.14em] bg-rc-berry/15 border border-rc-berry/30 rounded-pill px-1.5 py-0.5 flex-shrink-0">
          {badge}
        </span>
      )}
    </button>
  );
}
